"use client";

import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  FaArrowLeft,
  FaBars,
  FaCog,
  FaCube,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileInvoice,
  FaHome,
  FaInfoCircle,
  FaPlay,
  FaPowerOff,
  FaVideo,
} from "react-icons/fa";
import {
  ChevronDown,
  ContactRound,
  FileText,
  Menu,
  Pencil,
  ShieldAlert,
  ShieldQuestion,
  Plug,
  Truck,
  Users,
  KeyRound,
  CheckCircle2,
  Home,
  Scale,
  HelpCircle,
  Search,
  Phone,
  UserCheck,
  BarChart2,
  LogIn,
} from "lucide-react";
import ExperimentLaunchScreen from "./ExperimentLaunchScreen";
import ClipboardOnlyGuard from "./ClipboardOnlyGuard";
import { SIDEBAR_SUB_OPTIONS } from "./simulationSidebarSubOptions";
import GSTSimulation from "./GSTSimulation";

type Screen = "home" | "dashboard" | "invoice" | "cancel";
type Workflow = "invoice" | "cancel";

const latestUpdates = [
  {
    date: "01 OCT 2023",
    color: "blue",
    text: "Implementation of atleast 6 digit HSN in e-Invoices and e-Waybills has been deferred.",
  },
  {
    date: "19 SEP 2023",
    color: "green",
    text: "The tax payers, notified for generation of e-invoices and supplying to government departments / agencies, need to generate B2B e-Invoices with the GSTIN of the Government department / agency.",
  },
  {
    date: "11 SEP 2023",
    color: "orange",
    text: "2-Factor Authentication for all taxpayers with AATO above Rs 20 Cr is mandatory from 1st November 2023.",
  },
  {
    date: "11 SEP 2023",
    color: "red",
    text: "As per directions by GST Authority, a time limit of 30 days for reporting of invoices from date of invoice is imposed on e-invoice portals, and is applicable for taxpayers with AATO greater than or equal to 100 crores from 1st November 2023.",
  },
  {
    date: "09 AUG 2023",
    color: "purple",
    text: "Sub users can access e-Invoices generated by the main user.",
  },
];

interface GSTEInvoiceReplicaProps {
  initialScreen?: Screen;
  baseRoute?: string;
  launchTitle?: string;
  portalTitle?: string;
  companyName?: string;
  workflow?: Workflow;
  initialInvoiceMenuOpen?: boolean;
  initialShowLaunchScreen?: boolean;
  startOnDashboard?: boolean;
  highlightInvoiceMenu?: boolean;
  highlightBulkUpload?: boolean;
  highlightCancelMenu?: boolean;
  highlightPrintMenu?: boolean;
  bulkUploadRoute?: string;
}

type NavSuboption = {
  label: string;
  badge?: string;
  subText?: string;
  hasArrow?: boolean;
  children?: { label: string; subText?: string }[];
};

type NavTab = {
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  suboptions: NavSuboption[];
};

const navTabs: NavTab[] = [
  {
    label: "Home",
    icon: Home,
    suboptions: [],
  },
  {
    label: "Laws",
    icon: Scale,
    suboptions: [
      { label: "Rules" },
      { label: "Notifications", badge: "NEW" },
    ],
  },
  {
    label: "Help",
    icon: HelpCircle,
    suboptions: [
      {
        label: "User Manuals",
        hasArrow: true,
        children: [
          { label: "Web System" },
          { label: "Bulk Generation System" },
        ],
      },
      { label: "Videos" },
      {
        label: "FAQs",
        hasArrow: true,
        subText: "Updated!",
        children: [
          { label: "Legal / Procedure" },
          { label: "Technology / Portal / APIs", subText: "Updated!" },
        ],
      },
      {
        label: "Documents",
        hasArrow: true,
        children: [
          { label: "e-Invoice" },
          { label: "API Sandbox tool" },
          { label: "QR Code" },
          { label: "e-Com Operator" },
          { label: "e-invoicing – Detailed Overview" },
        ],
      },
      { label: "Presentations", hasArrow: true },
      {
        label: "Tools",
        hasArrow: true,
        children: [
          { label: "Bulk Generation Tools" },
          { label: "Verify QR Code App" },
        ],
      },
      {
        label: "Downloads",
        hasArrow: true,
        children: [
          { label: "Sample Signed e-Invoice Json" },
          { label: "Public keys", subText: "Updated!" },
        ],
      },
      { label: "Resolution for Common Errors" },
    ],
  },
  {
    label: "Search",
    icon: Search,
    suboptions: [
      { label: "Tax Payer / GSTIN" },
      { label: "e-Invoice Status of Taxpayer" },
      { label: "Verify Signed Invoice" },
      { label: "Products & Services" },
      { label: "Master Codes" },
      { label: "GST Suvidha Providers / ERPs" },
      { label: "Pincode" },
      { label: "Pin to Pin Distance" },
      { label: "GSTINs generating IRN", subText: "Updated!" },
    ],
  },
  {
    label: "Contact Us",
    icon: Phone,
    suboptions: [],
  },
  {
    label: "Registration",
    icon: UserCheck,
    suboptions: [
      { label: "Portal Login" },
      { label: "e-Invoice Enablement" },
    ],
  },
  {
    label: "Statistics",
    icon: BarChart2,
    suboptions: [],
  },
];

export default function GSTEInvoiceReplica({
  initialScreen = "home",
  baseRoute = "/simulations/gst/e-invoicing-1",
  launchTitle = "GST E-Invoice Simulation",
  portalTitle = "e-Invoice 1 Portal",
  companyName = "ABC XYZ Private Limited",
  workflow = "invoice",
  initialInvoiceMenuOpen = false,
  initialShowLaunchScreen = true,
  startOnDashboard = false,
  highlightInvoiceMenu = false,
  highlightBulkUpload = false,
  highlightCancelMenu = false,
  highlightPrintMenu = false,
  bulkUploadRoute,
}: GSTEInvoiceReplicaProps) {
  const router = useRouter();
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [isInvoiceMenuOpen, setIsInvoiceMenuOpen] = useState(initialInvoiceMenuOpen);
  const [openSidebarMenu, setOpenSidebarMenu] = useState<string | null>(null);
  const sidebarMenuItems = [
    { label: "MIS Reports", Icon: Menu },
    { label: "User Management", Icon: Users },
    { label: "API Registration", Icon: Plug },
    { label: "Change Password", Icon: KeyRound },
    { label: "Feedback on GePP", Icon: ShieldQuestion },
    { label: "Update Contact Details", Icon: ContactRound },
    { label: "Update", Icon: Pencil },
    { label: "e-Way Bill", Icon: Truck },
    { label: "2 Factor Authentication", Icon: ShieldAlert },
  ];
  const [showLaunchScreen, setShowLaunchScreen] = useState(initialShowLaunchScreen);
  const [isStartingExperiment, setIsStartingExperiment] = useState(false);
  const launchTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (launchTimerRef.current !== null) {
        window.clearTimeout(launchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!startOnDashboard) {
      return;
    }

    setScreen("dashboard");
    setIsInvoiceMenuOpen(true);
    setShowLaunchScreen(false);
    setIsStartingExperiment(false);
  }, [startOnDashboard]);

  const handleStartExperiment = () => {
    if (isStartingExperiment) return;
    setIsStartingExperiment(true);
    launchTimerRef.current = window.setTimeout(() => {
      setShowLaunchScreen(false);
      setIsStartingExperiment(false);
      launchTimerRef.current = null;
    }, 1500);
  };
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({
    username: "AIR",
    password: "IICPA@123",
    captcha: "",
  });
  const [cancelBasis, setCancelBasis] = useState<"ack" | "irn" | "">("");
  const [cancelIdentifier, setCancelIdentifier] = useState("");
  const [cancelReason, setCancelReason] = useState("Others");
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [showCancelDetails, setShowCancelDetails] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const invoiceRoute = `${baseRoute}/invoice`;

  const goToInvoiceBuilder = () => {
    router.push(invoiceRoute);
  };

  const goToBulkUpload = () => {
    if (!bulkUploadRoute) {
      return;
    }

    router.push(bulkUploadRoute);
  };

  const goToCancelFlow = () => {
    setScreen("cancel");
    setCancelBasis("");
    setCancelIdentifier("");
    setShowCancelDetails(false);
    setShowCancelSuccess(false);
  };

  const handleCancelGo = () => {
    if (!cancelIdentifier.trim() && cancelBasis === "ack") {
      setCancelIdentifier("146872643383543");
    }
    setShowCancelDetails(true);
    setShowCancelSuccess(false);
  };

  const handleCancelSubmit = () => {
    setShowCancelSuccess(true);
  };

  const handleCancelRetry = () => {
    setShowCancelDetails(true);
    setShowCancelSuccess(false);
    setCancelRemarks("");
    setCancelReason("Others");
  };

  const handleCancelExit = () => {
    setScreen("dashboard");
    setShowCancelDetails(false);
    setShowCancelSuccess(false);
  };

  const renderCancelDashboard = () => null;



  const renderHomeScreen = () => (
    <div className="min-h-screen bg-white">
      <div className="max-w-[1840px] mx-auto px-4 pt-6 pb-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xl font-semibold text-slate-900">
                GST Invoice preparation
              </span>
              <div className="w-40 bg-slate-200 rounded-full h-4 overflow-hidden">
                <div className="bg-blue-600 h-4 w-[30%]" />
              </div>
              <span className="text-xs font-semibold text-slate-700">30%</span>
            </div>
            <select className="border border-slate-300 rounded-md bg-white px-4 py-2 text-sm text-slate-700">
              <option>Language</option>
            </select>
          </div>
          <div className="text-lg font-semibold text-slate-900">
            e-invoicing
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-400 text-white font-bold">
              1850
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-white p-4 shadow-sm">
          <div className="rounded-xl border-l-4 border-cyan-700 bg-white px-4 py-3 text-slate-800">
            <div className="text-base leading-6">
              Go to Login under Menu and Use the following Credentials to login:
            </div>
            <div className="mt-2">User name: AIR</div>
            <div>Password: IICPA@123</div>
          </div>

          <div className="mt-3 rounded-2xl border border-white/80 bg-white/70 shadow-[0_20px_60px_rgba(15,23,42,0.12)]">
            <div className="relative bg-[#2a6890] px-6 py-6 text-white rounded-t-2xl">
              <div className="absolute inset-0 bg-white/18" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/85 text-[#1f6793] font-bold text-lg">
                    🇮🇳
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-70">
                      Goods and Services Tax
                    </div>
                    <div className="text-2xl font-semibold opacity-90">
                      e - INVOICE SYSTEM
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold opacity-90">
                    {portalTitle}
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right">
                  <div>
                    <div className="text-sm font-semibold opacity-75">
                      NATION TAX MARKET
                    </div>
                    <div className="text-xs opacity-60">GST</div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold opacity-75">
                      एनआईसी NIC
                    </div>
                    <div className="text-xs opacity-60">
                      National Informatics Centre
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-stretch border-b border-slate-200 bg-white/85">
              {navTabs.map((tab, index) => {
                const TabIcon = tab.icon;
                return (
                  <div key={tab.label} className="relative group min-w-[140px] flex-1 border-r border-slate-200 flex">
                    <button
                      className={`w-full h-full flex items-center justify-center gap-1.5 px-3 py-5 text-[13px] lg:text-[14px] font-semibold transition-colors ${
                        index === 0
                          ? "bg-[#cfe2ff] text-[#1b66a0]"
                          : "bg-white/90 text-slate-700 hover:bg-slate-50 hover:text-blue-600"
                      }`}
                    >
                      {TabIcon && <TabIcon size={14} className={index === 0 ? "text-[#1b66a0]" : "text-slate-500 group-hover:text-blue-500"} />}
                      <span>{tab.label}</span>
                      {tab.suboptions.length > 0 && (
                        <ChevronDown size={12} className="text-slate-400 group-hover:rotate-180 transition-transform duration-200" />
                      )}
                    </button>
                    {tab.suboptions.length > 0 && (
                      <div className="absolute left-0 top-full hidden group-hover:block w-[240px] bg-white border border-slate-200 shadow-xl z-[9999] text-left py-1 rounded-b-md">
                        {tab.suboptions.map((sub, sIdx) => (
                          <div
                            key={sIdx}
                            className="relative group/sub px-4 py-2.5 hover:bg-blue-50 text-[13px] font-semibold text-slate-700 hover:text-blue-600 cursor-pointer border-b border-slate-100 last:border-0 flex flex-col justify-center transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <span>{sub.label}</span>
                              {sub.badge && (
                                <span className="bg-blue-600 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded uppercase animate-pulse">
                                  {sub.badge}
                                </span>
                              )}
                              {sub.hasArrow && (
                                <span className="text-[10px] text-slate-400">▶</span>
                              )}
                            </div>
                            {sub.subText && (
                              <span className="text-[10px] text-red-500 font-bold mt-0.5">
                                {sub.subText}
                              </span>
                            )}
                            {sub.children && sub.children.length > 0 && (
                              <div className="absolute left-full top-0 hidden group-hover/sub:block w-[240px] bg-white border border-slate-200 shadow-xl z-[9999] py-1 rounded-md">
                                {sub.children.map((child, cIdx) => (
                                  <div
                                    key={cIdx}
                                    className="px-4 py-2.5 hover:bg-blue-50 text-[13px] font-semibold text-slate-700 hover:text-blue-600 cursor-pointer border-b border-slate-100 last:border-0 transition-colors"
                                  >
                                    <span>{child.label}</span>
                                    {child.subText && (
                                      <span className="block text-[10px] text-red-500 font-bold mt-0.5">
                                        {child.subText}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                onClick={() => setShowLoginModal(true)}
                className="min-w-[140px] border-l border-slate-200 bg-slate-50 px-6 py-5 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors flex items-center justify-center gap-1.5"
              >
                <LogIn size={14} className="text-slate-500" />
                <span>LOGIN</span>
              </button>
            </div>

            <div className="p-4">
              <div className="flex flex-col gap-4 xl:flex-row">
                <div className="flex-1">
                  <div className="relative overflow-hidden rounded-xl bg-white min-h-[640px] border border-slate-100">
                    <div className="absolute inset-0 bg-white/52 backdrop-blur-[1px]" />
                    <div className="absolute inset-0 opacity-20">
                      <div className="grid grid-cols-12 gap-3 h-full p-6">
                        {Array.from({ length: 120 }).map((_, index) => (
                          <div
                            key={index}
                            className="rounded border border-sky-200/60"
                          />
                        ))}
                      </div>
                    </div>

                    <div className="relative z-10 flex h-full items-center justify-center px-8 py-12">
                      <div className="relative w-full max-w-[1100px]">
                        <div className="flex items-center justify-between gap-4">
                          <div className="absolute left-0 top-20 hidden xl:block">
                            <div className="rounded-full border-8 border-sky-100 bg-white/75 p-5 shadow-lg">
                              <div className="text-6xl opacity-70">🧾</div>
                            </div>
                          </div>

                          <div className="absolute right-20 top-0 hidden xl:block">
                            <div className="rounded-full border-8 border-sky-100 bg-white/75 p-5 shadow-lg">
                              <div className="text-5xl opacity-70">🖥️</div>
                            </div>
                          </div>
                        </div>

                        <div className="mx-auto flex w-full max-w-[720px] items-center justify-center">
                          <div className="relative flex h-[320px] w-[320px] items-center justify-center rounded-full bg-[#33a8dd]/95 shadow-[0_30px_90px_rgba(37,99,235,0.2)]">
                            <div className="flex h-[150px] w-[190px] items-center justify-center rounded-[18px] bg-[#23292f]/95 shadow-inner">
                              <div className="rounded-xl bg-white px-8 py-6 text-[34px] font-light text-slate-700 shadow-sm opacity-95">
                                E-Invoice
                              </div>
                            </div>
                            <div className="absolute -bottom-8 h-6 w-56 rounded-full bg-slate-700" />
                            <div className="absolute -bottom-16 left-1/2 flex -translate-x-1/2 gap-4">
                              <div className="h-10 w-10 rounded-full bg-[#ffe0b3]" />
                              <div className="h-10 w-10 rounded-full bg-[#ffe0b3]" />
                            </div>
                            <button
                              onClick={() => setShowLoginModal(true)}
                              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl bg-[#1244b8] px-10 py-5 text-3xl font-bold tracking-wide text-white shadow-lg hover:bg-[#0f3a9a]"
                            >
                              START EXPERIMENT
                            </button>
                          </div>
                        </div>

                        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_300px]">
                          <div className="flex flex-wrap gap-6">
                            <div className="flex h-24 w-24 items-center justify-center rounded-full border-8 border-sky-100 bg-white/80 shadow-md">
                              <div className="text-4xl opacity-70">📄</div>
                            </div>
                            <div className="flex h-28 w-[220px] flex-col justify-center rounded-xl border border-slate-200 bg-white/85 p-4 shadow-sm">
                              <div className="text-lg font-semibold text-slate-800 opacity-75">
                                INVOICE
                              </div>
                              <div className="text-sm text-slate-500 opacity-70">
                                Sample document
                              </div>
                            </div>
                            <div className="flex h-28 w-[180px] flex-col justify-center rounded-xl border border-slate-200 bg-white/85 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-slate-800 opacity-75">
                                CREDIT NOTE
                              </div>
                              <div className="text-sm text-slate-500 opacity-70">
                                Document
                              </div>
                            </div>
                            <div className="flex h-28 w-[180px] flex-col justify-center rounded-xl border border-slate-200 bg-white/85 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-slate-800 opacity-75">
                                DEBIT NOTE
                              </div>
                              <div className="text-sm text-slate-500 opacity-70">
                                Document
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="rounded-full bg-amber-400 p-3 shadow-md">
                              <FaPlay className="text-amber-900" />
                            </div>
                            <div className="flex-1 rounded-xl border border-slate-200 bg-white/85 p-4 shadow-sm">
                              <div className="text-sm font-semibold text-slate-800 opacity-75">
                                Invoice
                              </div>
                              <div className="text-xs text-slate-500 opacity-70">
                                Portal module
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-b-xl bg-white/90 px-6 py-4 text-[17px] leading-7 text-blue-700">
                    The e-Invoice System is for GST registered person for
                    uploading all the B2B invoices to the Invoice Registration
                    Portal (IRP). The IRP generates and returns a unique
                    Invoice Reference Number (IRN), digitally signed e-invoice
                    and QR code to the user.
                  </div>
                </div>

                <div className="w-full xl:w-[360px]">
                  <div className="rounded-2xl border border-slate-200 bg-white/90 p-6 shadow-sm">
                    <h3 className="text-2xl font-extrabold text-slate-500">
                      LATEST UPDATES
                    </h3>
                    <div className="mt-5 space-y-6">
                      {latestUpdates.map((item) => (
                        <div
                          key={`${item.date}-${item.text}`}
                          className={`border-l-4 pl-4 ${
                            item.color === "blue"
                              ? "border-blue-500"
                              : item.color === "green"
                              ? "border-green-500"
                              : item.color === "orange"
                              ? "border-orange-500"
                              : "border-red-500"
                          }`}
                        >
                          <div className="mb-1 text-sm font-semibold text-slate-400">
                            {item.date}
                          </div>
                          <p className="text-base font-semibold leading-6 text-blue-700/90">
                            • {item.text}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 text-right text-lg font-semibold text-blue-600">
                      Previous Updates
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-xl bg-[#245f87] px-6 py-5 text-white">
              <div className="text-lg font-semibold uppercase tracking-wide opacity-90">
                  Important Links
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-[520px] rounded-2xl bg-[#dfe7f3] p-6 shadow-[0_30px_100px_rgba(15,23,42,0.28)]">
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute left-4 top-4 text-3xl font-bold text-[#274d80]"
            >
              ×
            </button>

            <div className="flex flex-col items-center gap-3 pt-6">
              <div className="text-6xl">🇮🇳</div>
              <div className="text-2xl font-extrabold text-red-600">
                E-INVOICE SYSTEM LOGIN
              </div>
            </div>

            <div className="mt-5 rounded-md border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-700 shadow-inner">
              <div className="font-semibold text-slate-800">Login Details</div>
              <div className="mt-1">User name: AIR</div>
              <div>Password: IICPA@123</div>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-lg text-slate-700">
                  User name
                </label>
                <input
                  value={loginData.username}
                  onChange={(e) =>
                    setLoginData((prev) => ({ ...prev, username: e.target.value }))
                  }
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-base shadow-inner outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-lg text-slate-700">
                  Password
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) =>
                    setLoginData((prev) => ({ ...prev, password: e.target.value }))
                  }
                  className="w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-base shadow-inner outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <div className="mb-1 text-lg text-slate-700">
                  Enter Above Captcha
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex h-14 items-center justify-center rounded-md border border-slate-300 bg-white px-4 font-mono text-3xl font-black tracking-tight">
                    P2B7(Y
                  </div>
                  <input
                    value={loginData.captcha}
                    onChange={(e) =>
                      setLoginData((prev) => ({ ...prev, captcha: e.target.value }))
                    }
                    className="rounded-md border border-slate-300 bg-white px-4 py-3 text-base shadow-inner outline-none focus:border-blue-500"
                    placeholder="Enter captcha"
                  />
                </div>
              </div>

              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setScreen("dashboard");
                }}
                className="w-full rounded-md border-2 border-red-500 bg-[#0d63d7] py-3 text-2xl font-semibold text-white transition-colors hover:bg-[#0a54ba]"
              >
                LOGIN
              </button>

              <div className="flex items-center justify-between pt-2 text-base text-blue-700">
                <span>Forgot Password ?</span>
                <span>Forgot Username ?</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDashboard = () => (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-50 w-full">
        <div className="bg-[#ec1e18] px-4 py-2.5 text-center text-[14px] font-medium leading-tight text-white sm:text-[16px]">
          This is a Simulation. Use For Educational Purposes ONLY.
        </div>

        <div className="bg-white px-0 pb-0 pt-0">
          <div className="bg-[#24668f] text-white">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img
                    src="/images/simulations/satyamev-jayate.jpg"
                    alt="Satyamev Jayate emblem"
                    className="h-[54px] w-[54px] object-contain lg:h-[60px] lg:w-[60px]"
                  />
                  <div>
                    <div className="text-[10px] font-semibold uppercase leading-tight lg:text-[11px]">
                      GOODS AND SERVICES TAX
                    </div>
                    <div className="text-[14px] font-bold uppercase leading-tight lg:text-[16px]">
                      e - INVOICE SYSTEM
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-[14px] font-bold lg:text-[16px]">{portalTitle}</div>
                </div>

                <div className="flex items-center gap-4 lg:gap-6">
                  <img
                    src="/images/simulations/red-1-logo.png"
                    alt="Nation Tax Market logo"
                    className="h-[34px] w-auto object-contain lg:h-[40px]"
                  />
                  <img
                    src="/images/simulations/nic-logo-remove-1.png"
                    alt="NIC logo"
                    className="h-[34px] w-auto object-contain lg:h-[40px]"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/15 bg-[#ccd4e9] px-4 py-2.5 text-[12px] text-slate-700 lg:text-[14px]">
              <div className="flex items-center justify-between gap-4 whitespace-nowrap">
                <div className="text-left whitespace-nowrap">
                  GSTIN: 09ABCDE1234F1Z5 - Name: {companyName}
                </div>
                <div className="whitespace-nowrap">Account : Main User</div>
                <div className="whitespace-nowrap">Client IP:1.1.1.1</div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-0 pt-0">
        <div className="overflow-hidden rounded-sm border border-slate-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.12)]">
          <div className="flex min-h-[calc(100vh-128px)] w-full">
            <aside className="sticky top-[175px] flex h-[calc(100vh-128px)] w-[260px] flex-col bg-[#d7def0]">
              <div className="border-b border-white/60 px-4 py-3">
                <div className="flex w-full items-center gap-3 rounded-sm bg-[#2f7fd3] px-3 py-2 text-[12px] font-semibold text-white shadow-sm">
                  <span className="text-lg leading-none">e</span>
                  <span>e-Way bill Portal</span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div
                  className={
                    isInvoiceMenuOpen || highlightInvoiceMenu
                      ? "overflow-hidden border border-red-500 bg-[#d7def0]"
                      : ""
                  }
                >
                  <button
                    onClick={() => setIsInvoiceMenuOpen((current) => !current)}
                    className={`flex w-full items-center justify-between px-4 py-3 text-left text-[12px] font-medium text-slate-700 ${
                      highlightInvoiceMenu || isInvoiceMenuOpen
                        ? "border border-red-500 bg-[#cbd3f2]"
                        : "border-2 border-transparent bg-[#d7def0]"
                    } hover:border-red-400 hover:bg-[#e8edf8] focus-visible:border-red-500 focus-visible:outline-none`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={15} className="text-slate-600" />
                      <span>e-Invoice</span>
                    </div>
                    <ChevronDown
                      size={14}
                      className={`text-slate-500 transition-transform ${
                        isInvoiceMenuOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isInvoiceMenuOpen && (
                    <div className="bg-[#d7def0]">
                      <button
                        onClick={goToBulkUpload}
                        className={`flex w-full items-center justify-between bg-[#d7def0] px-4 py-3 text-left text-[12px] font-medium text-slate-700 hover:bg-[#e8edf8] focus-visible:outline-none ${
                          highlightBulkUpload
                            ? "border border-red-500"
                            : "border-t border-white/60"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={15} className="text-slate-600" />
                          <span>Bulk Upload</span>
                        </div>
                      </button>
                      <button 
                        onClick={goToCancelFlow}
                        className={`flex w-full items-center justify-between bg-[#d7def0] px-4 py-3 text-left text-[12px] font-medium text-slate-700 hover:bg-[#e8edf8] focus-visible:outline-none ${
                          highlightCancelMenu
                            ? "border border-red-500"
                            : workflow === "cancel"
                              ? "border-b border-white/60"
                              : "border-t border-white/60"
                        }`}>
                        <div className="flex items-center gap-3">
                          <FileText size={15} className="text-slate-600" />
                          <span>Cancel</span>
                        </div>
                      </button>
                      <button
                        className={`flex w-full items-center justify-between bg-[#d7def0] px-4 py-3 text-left text-[12px] font-medium text-slate-700 hover:bg-[#e8edf8] focus-visible:outline-none ${
                          highlightPrintMenu
                            ? "border border-red-500"
                            : "border-t border-white/60 hover:border-red-500"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={15} className="text-slate-600" />
                          <span>Print</span>
                        </div>
                      </button>
                      <button className="flex w-full items-center justify-between border-t border-white/60 bg-[#d7def0] px-4 py-3 text-left text-[12px] font-medium text-slate-700 hover:bg-[#e8edf8]">
                        <div className="flex items-center gap-3">
                          <FileText size={15} className="text-slate-600" />
                          <span>Bulk IRN Cancel</span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {sidebarMenuItems.map(({ label, Icon }) => {
                  const subOptions = SIDEBAR_SUB_OPTIONS[label] || [];
                  const isOpen = openSidebarMenu === label;
                  return (
                    <div key={label}>
                      <button
                        onClick={() =>
                          setOpenSidebarMenu(isOpen ? null : label)
                        }
                        className="flex w-full items-center justify-between border-b border-white/60 bg-[#d7def0] px-4 py-3 text-left text-[12px] font-medium text-slate-700 hover:bg-[#e8edf8]"
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={15} className="text-slate-600" />
                          <span>{label}</span>
                        </div>
                        {subOptions.length ? (
                          <ChevronDown
                            size={14}
                            className={`text-slate-500 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        ) : null}
                      </button>
                      {isOpen && subOptions.length > 0 && (
                        <div className="border-b border-white/60 bg-[#e8edf8]">
                          {subOptions.map((option) => (
                            <button
                              key={option}
                              className="flex w-full items-center gap-2 px-4 py-2 pl-10 text-left text-[12px] text-slate-600 hover:bg-[#dde5f5] hover:text-slate-900"
                            >
                              <span className="h-1 w-1 flex-shrink-0 rounded-full bg-slate-400" />
                              <span>{option}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </aside>

            <main className="flex-1 px-3 py-3 lg:px-4">
              <div className="w-full">
                <div className="grid gap-4">
                  <section className="rounded-b-xl bg-white shadow-sm">
                    <div className="px-6 pt-3 text-center text-[18px] font-extrabold text-[#1b66a0] lg:text-[22px]">
                      Dash Board
                    </div>

                    <div className="mt-3 grid gap-4 px-4 md:grid-cols-2 lg:px-6">
                      <div className="rounded border border-blue-200">
                        <div className="flex items-center justify-between bg-blue-500 px-4 py-2.5 text-white">
                          <div className="text-[12px] font-semibold">Generations</div>
                          <div className="text-[12px]">◔</div>
                        </div>
                        <div className="bg-white px-4 py-3 text-[12px] text-slate-700 lg:text-[13px]">
                          <div className="flex justify-between border-b border-slate-200 py-1.5">
                            <span className="underline">Yesterday</span>
                            <span className="underline">0</span>
                          </div>
                          <div className="flex justify-between py-1.5">
                            <span className="underline">During This month</span>
                            <span className="underline">67</span>
                          </div>
                        </div>
                      </div>

                      <div className="rounded border border-red-200">
                        <div className="flex items-center justify-between bg-red-500 px-4 py-2.5 text-white">
                          <div className="text-[12px] font-semibold">Cancelled</div>
                          <div className="text-[12px]">↪</div>
                        </div>
                        <div className="bg-white px-4 py-3 text-[12px] text-slate-700 lg:text-[13px]">
                          <div className="flex justify-between border-b border-slate-200 py-1.5">
                            <span className="underline">Yesterday</span>
                            <span className="underline">0</span>
                          </div>
                          <div className="flex justify-between py-1.5">
                            <span className="underline">During This month</span>
                            <span className="underline">0</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mx-4 mt-4 rounded border border-slate-200 bg-white lg:mx-6">
                      <div className="border-b border-slate-200 px-4 py-2 text-[13px] font-semibold text-cyan-500 lg:text-[14px]">
                        Notes:
                      </div>
                      <div className="space-y-2.5 px-4 py-3 text-[12px] leading-5 text-slate-700 lg:px-6 lg:text-[13px] lg:leading-6">
                        <div className="flex gap-3">
                          <span>•</span>
                          <span>
                            The Bulk IRN generation facility has been enabled.
                            You may download the tools from the portal under Help
                            -&gt; Tools.
                          </span>
                        </div>
                        <div className="flex gap-3">
                          <span>•</span>
                          <span>
                            The e-Waybills generated in this portal will be
                            reflected in the e-waybill system. To Update Part-B
                            details, cancel or extend, you may login to
                            e-waybill system with same credentials.
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 px-4 py-4 lg:px-6">
                      <button
                        onClick={() => setScreen("home")}
                        className="rounded-md border border-slate-300 bg-white px-3.5 py-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Back to Portal
                      </button>
                      <button
                        onClick={workflow === "cancel" ? goToCancelFlow : goToInvoiceBuilder}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-[11px] font-semibold text-white ${
                          workflow === "cancel"
                            ? "bg-red-600 hover:bg-red-700"
                            : "bg-blue-700 hover:bg-blue-800"
                        }`}
                      >
                        {workflow === "cancel" ? "Continue to Cancel Flow" : "Continue to Invoice Builder"}
                      </button>
                    </div>
                  </section>

                </div>

                <div className="mt-3 flex items-center justify-between bg-[#1d5d83] px-4 py-2 text-white">
                  <div className="text-[11px] lg:text-[12px]">Version 1.03</div>
                  <div className="text-[11px] lg:text-[12px]">
                    © 2022 - Powered By National Informatics Centre.
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCancelScreen = () => (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-[100000] w-full">
        <div className="relative z-[100001] bg-[#ec1e18] px-4 py-2.5 text-center text-[14px] font-medium leading-tight text-white sm:text-[16px]">
          This is a Simulation. Use For Educational Purposes ONLY.
        </div>

        <div className="relative z-[100001] bg-white px-0 pb-0 pt-0">
          <div className="bg-[#24668f] text-white">
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img
                    src="/images/simulations/satyamev-jayate.jpg"
                    alt="Satyamev Jayate emblem"
                    className="h-[54px] w-[54px] object-contain lg:h-[60px] lg:w-[60px]"
                  />
                  <div>
                    <div className="text-[10px] font-semibold uppercase leading-tight lg:text-[11px]">
                      GOODS AND SERVICES TAX
                    </div>
                    <div className="text-[14px] font-bold uppercase leading-tight lg:text-[16px]">
                      e - INVOICE SYSTEM
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <div className="text-[14px] font-bold lg:text-[16px]">{portalTitle}</div>
                </div>

                <div className="flex items-center gap-4 lg:gap-6">
                  <img
                    src="/images/simulations/red-1-logo.png"
                    alt="Nation Tax Market logo"
                    className="h-[34px] w-auto object-contain lg:h-[40px]"
                  />
                  <img
                    src="/images/simulations/nic-logo-remove-1.png"
                    alt="NIC logo"
                    className="h-[34px] w-auto object-contain lg:h-[40px]"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-white/15 bg-[#ccd4e9] px-4 py-2.5 text-[12px] text-slate-700 lg:text-[14px]">
              <div className="flex items-center justify-between gap-4 whitespace-nowrap">
                <div className="text-left whitespace-nowrap">
                  GSTIN: 09ABCDE1234F1Z5 - Name: {companyName}
                </div>
                <div className="whitespace-nowrap">Account : Main User</div>
                <div className="whitespace-nowrap">Client IP:1.1.1.1</div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-0 pt-0">
        <div className="overflow-hidden rounded-sm border border-slate-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.12)]">
          <div className="flex min-h-[calc(100vh-128px)] w-full">

            <main className="flex-1 px-3 py-3 lg:px-4">
              <div className="w-full">
                <div className="grid gap-4">
                  <section className="rounded-b-xl bg-white shadow-sm">
                    <div className="flex items-center justify-between gap-4 px-4 pt-3">
                      <button
                        onClick={() => setScreen("dashboard")}
                        className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                      >
                        <FaArrowLeft size={12} />
                        Back
                      </button>
                      <div className="flex-1 text-center text-[18px] font-extrabold text-[#1b66a0] lg:text-[22px]">
                        e-Invoice Cancel
                      </div>
                      <div className="w-[80px]" />
                    </div>

                    <div className="mt-4 flex items-center justify-center gap-6 text-[12px] font-bold text-slate-700 lg:text-[13px]">
                      <span>Based On :</span>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="radio"
                          name="cancelBasis"
                          checked={cancelBasis === "ack"}
                          onChange={() => {
                            setCancelBasis("ack");
                            setCancelIdentifier("");
                          }}
                          className="h-3.5 w-3.5"
                        />
                        <span>Ack No.</span>
                      </label>
                      <label className="flex items-center gap-1.5 opacity-50 cursor-not-allowed">
                        <input
                          type="radio"
                          name="cancelBasis"
                          checked={cancelBasis === "irn"}
                          disabled
                          className="h-3.5 w-3.5 cursor-not-allowed"
                        />
                        <span>IRN</span>
                      </label>
                    </div>

                    {cancelBasis === "ack" && (
                      <div className="mt-5 flex items-center justify-center gap-3 px-4">
                        <label className="text-[12px] font-bold text-slate-700 lg:text-[13px]">
                          Enter Ack. No. :
                        </label>
                        <input
                          value={cancelIdentifier}
                          onChange={(e) => setCancelIdentifier(e.target.value)}
                          className="w-full max-w-[320px] rounded border border-slate-400 px-3 py-1 text-[12px] text-slate-800 outline-none focus:border-blue-500 lg:text-[13px]"
                        />
                        <button
                          onClick={handleCancelGo}
                          className="rounded bg-[#1e66a0] px-4 py-1 text-[12px] font-semibold text-white hover:bg-[#1a5585]"
                        >
                          Go
                        </button>
                        <button
                          onClick={() => setScreen("dashboard")}
                          className="rounded bg-[#ec1e18] px-4 py-1 text-[12px] font-semibold text-white hover:bg-[#d01a15]"
                        >
                          Exit
                        </button>
                      </div>
                    )}

                    {showCancelDetails && (
                      <div className="relative mx-auto mt-8 max-w-[1200px] border border-slate-200 bg-white p-0">
                        {showCancelSuccess && (
                          <div className="absolute inset-x-0 bottom-0 top-[44px] z-[9999] flex items-center justify-center bg-white/25 backdrop-blur-[1px]">
                            <div className="flex flex-col items-center gap-5">
                              <div className="flex h-28 w-28 items-center justify-center rounded-full bg-white shadow-[0_18px_40px_rgba(15,23,42,0.15)]">
                                <img
                                  src="/images/simulations/tick.png"
                                  alt="Success"
                                  className="h-20 w-20 object-contain"
                                />
                              </div>
                              <div className="rounded-md border-2 border-red-600 px-6 py-2 text-[34px] font-black uppercase tracking-[0.18em] text-red-600 -rotate-6 shadow-lg bg-white/90">
                                Cancelled
                              </div>
                              <button
                                onClick={handleCancelRetry}
                                className="rounded-md bg-[#d9534f] px-8 py-2 text-[14px] font-bold text-white shadow-md transition-colors hover:bg-[#c9302c]"
                              >
                                Retry
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="bg-[#1b66a0] px-4 py-1 text-center text-[16px] font-bold text-white">
                          e-Invoice Cancel
                        </div>

                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="text-[16px] font-black text-slate-800 lg:text-[18px]">
                                09ABCDE1234F1Z5
                              </div>
                              <div className="text-[16px] font-black text-slate-800 lg:text-[18px]">
                                ABC XYZ Private Limited
                              </div>
                            </div>
                            <div className="flex-shrink-0 rounded-[4px] border border-slate-200 bg-white p-2 shadow-sm">
                              <div className="grid h-[210px] w-[210px] place-items-center bg-white">
                                <img
                                  src="/images/simulations/qr-gst.webp"
                                  alt="GST QR code"
                                  className="h-[180px] w-[180px] object-contain"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="relative mt-4 overflow-hidden rounded border border-slate-200 text-[11px] lg:text-[12px]">

                            <table className="w-full border-collapse">
                              <tbody>
                                <tr className="bg-slate-50 font-bold border-b border-slate-200">
                                  <td colSpan={4} className="px-2 py-1">1.e-Invoice Details</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td className="px-2 py-1 font-bold">IRN :</td>
                                  <td className="px-2 py-1 truncate max-w-[200px]">uncWIpXGNqaGmHkd5U3v4fYXl6OW6s7kJAjOcHe6Inj6wo6rQZHfOEnEIPP1jAY</td>
                                  <td className="px-2 py-1 font-bold">Ack. No :</td>
                                  <td className="px-2 py-1">146872643383543</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td colSpan={2} className="px-2 py-1 font-bold"></td>
                                  <td className="px-2 py-1 font-bold">Ack. Date :</td>
                                  <td className="px-2 py-1">09/10/2023</td>
                                </tr>
                                <tr className="bg-slate-50 font-bold border-b border-slate-200">
                                  <td colSpan={4} className="px-2 py-1">2.Transaction Details</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td className="px-2 py-1 font-bold">Supply Type Code :</td>
                                  <td className="px-2 py-1">B2B</td>
                                  <td className="px-2 py-1 font-bold">Document No :</td>
                                  <td className="px-2 py-1">STC/23-24/masdks</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td className="px-2 py-1 font-bold">Place of Supply :</td>
                                  <td className="px-2 py-1">UP</td>
                                  <td className="px-2 py-1 font-bold">Document Date :</td>
                                  <td className="px-2 py-1">09/10/2023</td>
                                </tr>
                                <tr className="bg-slate-50 font-bold border-b border-slate-200">
                                  <td colSpan={4} className="px-2 py-1">3.Party Details</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td colSpan={2} className="px-2 py-1 font-bold">Supplier</td>
                                  <td colSpan={2} className="px-2 py-1 font-bold">Recipient</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td colSpan={2} className="px-2 py-1">
                                    GSTIN : 09ABCDE1234F1Z5 <br />
                                    ABC XYZ Private Limited
                                  </td>
                                  <td colSpan={2} className="px-2 py-1">
                                    GSTIN : 09ZZZZZ6789K1Z2 <br />
                                    ZZZZ Enterprises LLP
                                  </td>
                                </tr>
                                <tr className="bg-slate-50 font-bold border-b border-slate-200">
                                  <td colSpan={4} className="px-2 py-1">4.Details of Goods / Services</td>
                                </tr>
                              </tbody>
                            </table>
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-slate-50 text-[11px] font-bold border-b border-slate-200">
                                  <td className="px-2 py-1 border-r border-slate-200">SLNO</td>
                                  <td className="px-2 py-1 border-r border-slate-200">ITEM DESCRIPTION</td>
                                  <td className="px-2 py-1 border-r border-slate-200">QUANTITY</td>
                                  <td className="px-2 py-1 border-r border-slate-200">UNIT PRICE</td>
                                  <td className="px-2 py-1">TOTAL</td>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b border-slate-200">
                                  <td className="px-2 py-1 border-r border-slate-200">1</td>
                                  <td className="px-2 py-1 border-r border-slate-200">SILK GLAMOR P0 BASE - 4L</td>
                                  <td className="px-2 py-1 border-r border-slate-200">6 NOS</td>
                                  <td className="px-2 py-1 border-r border-slate-200">1907.63</td>
                                  <td className="px-2 py-1">13506.02</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                  <td className="px-2 py-1 border-r border-slate-200">2</td>
                                  <td className="px-2 py-1 border-r border-slate-200">SILK GLAMOR N2 BASE - 3.6L</td>
                                  <td className="px-2 py-1 border-r border-slate-200">1 NOS</td>
                                  <td className="px-2 py-1 border-r border-slate-200">1950</td>
                                  <td className="px-2 py-1">2301</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className="mt-4 grid gap-4 border-t border-slate-200 px-4 py-4 text-[12px] text-slate-800 lg:grid-cols-[1fr_auto_1fr] lg:items-center bg-white">
                            <div>
                              <div className="font-semibold">Generated By : <span className="font-normal">09ABCDE1234F1Z5</span></div>
                              <div className="font-semibold">Print Date : <span className="font-normal">08-05-2026 16:46:19</span></div>
                            </div>
                            <div className="text-center">
                              <img
                                src="/images/simulations/barcode-image.jpg"
                                alt="Barcode"
                                className="mx-auto h-[52px] w-[140px] object-contain"
                              />
                              <div className="mt-1 text-[10px] text-slate-500">182314594723038</div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">Digitally Signed by NIC-IRP</div>
                              <div>on: 09-10-2023 18:52:00</div>
                            </div>
                          </div>

                          <div className="mt-8 mb-4 flex justify-center">
                            <div className="w-full max-w-[600px] rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                              {showCancelSuccess ? (
                                <div className="flex flex-col items-center gap-6 py-6 animate-in fade-in zoom-in duration-500">
                                  <div className="relative">
                                    <div className="absolute inset-0 bg-green-100 rounded-full scale-150 opacity-20 animate-ping" />
                                    <div className="relative w-24 h-24 bg-green-50 rounded-full flex items-center justify-center border-4 border-green-100 shadow-inner">
                                      <img 
                                        src="/images/simulations/tick.png" 
                                        alt="Success" 
                                        className="w-14 h-14 object-contain" 
                                      />
                                    </div>
                                  </div>
                                  <div className="text-center">
                                    <h3 className="text-2xl font-black text-slate-800">Cancelled Successfully!</h3>
                                    <p className="text-slate-500 font-medium mt-1">The e-Invoice status has been updated.</p>
                                  </div>
                                  <div className="flex items-center gap-4 mt-2">
                                    <button
                                      onClick={handleCancelRetry}
                                      className="group relative overflow-hidden rounded-[4px] bg-[#337ab7] px-10 py-2 text-[14px] font-bold text-white shadow-lg transition-all hover:bg-[#286090] hover:shadow-blue-200/50 active:scale-95"
                                    >
                                      <span className="relative z-10">Retry</span>
                                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                    <button
                                      onClick={handleCancelExit}
                                      className="group relative overflow-hidden rounded-[4px] bg-[#d9534f] px-10 py-2 text-[14px] font-bold text-white shadow-lg transition-all hover:bg-[#c9302c] hover:shadow-red-200/50 active:scale-95"
                                    >
                                      <span className="relative z-10">Exit</span>
                                      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="grid grid-cols-[120px_1fr] items-center gap-4">
                                    <label className="text-[12px] font-bold text-slate-800">
                                      Cancel Reason :
                                    </label>
                                    <select
                                      value={cancelReason}
                                      onChange={(e) => setCancelReason(e.target.value)}
                                      className="w-full rounded-[4px] border border-slate-300 bg-white px-3 py-1.5 text-[12px] text-slate-700 outline-none focus:border-blue-500 transition-colors"
                                    >
                                      <option>Others</option>
                                      <option>Duplicate</option>
                                      <option>Wrong Invoice</option>
                                      <option>Order Cancelled</option>
                                    </select>

                                    <label className="self-start pt-1.5 text-[12px] font-bold text-slate-800">
                                      Remarks :
                                    </label>
                                    <textarea
                                      name="cancelRemarks"
                                      data-allow-typing="true"
                                      value={cancelRemarks}
                                      onInput={(e) =>
                                        setCancelRemarks(
                                          (e.currentTarget as HTMLTextAreaElement).value,
                                        )
                                      }
                                      onChange={(e) => setCancelRemarks(e.target.value)}
                                      rows={3}
                                      placeholder="Enter cancellation remarks..."
                                      autoComplete="off"
                                      spellCheck={false}
                                      className="w-full cursor-text rounded-[4px] border border-slate-300 bg-white px-3 py-2 text-[12px] text-slate-700 outline-none focus:border-blue-500 resize-none transition-colors min-h-[92px]"
                                    />
                                  </div>

                                  <div className="mt-6 flex items-center justify-center gap-2">
                                    <button
                                      onClick={handleCancelSubmit}
                                      className="rounded-[4px] bg-[#337ab7] px-8 py-2 text-[12px] font-bold text-white shadow-sm transition-all hover:bg-[#286090] active:scale-95"
                                    >
                                      Submit
                                    </button>
                                    <button
                                      onClick={handleCancelExit}
                                      className="rounded-[4px] bg-[#d9534f] px-8 py-2 text-[12px] font-bold text-white shadow-sm transition-all hover:bg-[#c9302c] active:scale-95"
                                    >
                                      Exit
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="mt-8 flex items-center justify-between bg-[#1d5d83] px-4 py-2 text-white">
                      <div className="text-[11px] lg:text-[12px]">Version 1.03</div>
                      <div className="text-[11px] lg:text-[12px]">
                        © 2022 - Powered By National Informatics Centre.
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInvoiceBuilder = () => (
    <div className="min-h-screen bg-slate-50">
        <div className="border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-[1800px] items-center justify-between">
          <button
            onClick={() => router.push(baseRoute)}
            className="rounded-md border border-slate-300 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Back to Portal Replica
          </button>
          <div className="text-lg font-semibold text-slate-700">
            GST E-Invoice Builder
          </div>
          <div className="text-sm text-slate-500">
            Route: {invoiceRoute}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1800px] p-4">
        <GSTSimulation />
      </div>
    </div>
  );

  if (screen === "invoice") {
    return renderInvoiceBuilder();
  }

  return (
    <div className={`relative min-h-screen ${showLaunchScreen ? "overflow-hidden" : ""}`}>
      <ClipboardOnlyGuard />
      {screen === "home"
        ? renderHomeScreen()
        : screen === "dashboard"
        ? renderDashboard()
        : screen === "cancel"
        ? renderCancelScreen()
        : renderInvoiceBuilder()}

      {showLaunchScreen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden px-4 text-white backdrop-blur-[4px]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.38),transparent_28%),radial-gradient(circle_at_top_right,rgba(59,130,246,0.18),transparent_24%),radial-gradient(circle_at_center,rgba(255,255,255,0.18),transparent_42%),linear-gradient(135deg,rgba(255,255,255,0.42)_0%,rgba(233,241,252,0.28)_35%,rgba(255,255,255,0.18)_100%)]" />
          <div className="absolute inset-0 bg-white/8" />
          <div className="absolute inset-0 opacity-[0.04]">
            <div className="grid h-full grid-cols-12 gap-4 p-6">
              {Array.from({ length: 132 }).map((_, index) => (
                <div key={index} className="rounded-md border border-slate-500/20" />
              ))}
            </div>
          </div>

          <div className="relative z-10 flex h-full w-full items-center justify-center">
            <button
              onClick={handleStartExperiment}
              disabled={isStartingExperiment}
              className="inline-flex min-h-[66px] w-[min(78vw,32rem)] items-center justify-center rounded-[22px] bg-[#1f4eb8] px-6 text-[16px] font-bold uppercase tracking-[0.18em] text-white shadow-[0_14px_34px_rgba(31,78,184,0.28)] transition-transform duration-200 hover:scale-[1.02] hover:bg-[#18439c] disabled:cursor-wait disabled:opacity-90 sm:min-h-[72px] sm:w-[min(72vw,34rem)] sm:px-7 sm:text-[18px]"
              aria-label="Start experiment"
            >
              {isStartingExperiment ? "EXPERIMENTING..." : "START EXPERIMENT"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
